import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: async () => import('@/views/LoginView.vue'),
    meta: { public: true, title: 'Sign in' },
  },
  {
    path: '/',
    component: async () => import('@/layouts/AdminShell.vue'),
    children: [
      {
        path: '',
        name: 'dashboard',
        component: async () => import('@/views/DashboardView.vue'),
        meta: { title: 'Dashboard' },
      },
      {
        path: 'users',
        name: 'users',
        component: async () => import('@/views/UsersView.vue'),
        meta: { title: 'Users', permission: 'users.view' },
      },
      {
        path: 'users/:id',
        name: 'user-profile',
        component: async () => import('@/views/UserProfileView.vue'),
        meta: { title: 'User profile', permission: 'users.view' },
      },
      {
        path: 'payments',
        name: 'payments',
        component: async () => import('@/views/PaymentsView.vue'),
        meta: { title: 'Payments', permission: 'payments.view' },
      },
      {
        path: 'withdrawals',
        name: 'withdrawals',
        component: async () => import('@/views/WithdrawalsView.vue'),
        meta: { title: 'Withdrawals', permission: 'withdrawals.approve' },
      },
      {
        path: 'moderation',
        name: 'moderation',
        component: async () => import('@/views/ModerationView.vue'),
        meta: { title: 'Moderation', permission: 'uploads.moderate' },
      },
      {
        path: 'reports',
        name: 'reports',
        component: async () => import('@/views/ReportsView.vue'),
        meta: { title: 'Reports', permission: 'moderation.review' },
      },
      {
        path: 'settings',
        name: 'settings',
        component: async () => import('@/views/SettingsView.vue'),
        meta: { title: 'Settings', permission: 'settings.manage' },
      },
      {
        path: 'integrations',
        name: 'integrations',
        component: async () => import('@/views/IntegrationsView.vue'),
        meta: { title: 'Integrations', permission: 'settings.manage' },
      },
      {
        path: 'jobs',
        name: 'jobs',
        component: async () => import('@/views/JobsView.vue'),
        meta: { title: 'Scheduler', permission: 'settings.manage' },
      },
      {
        path: 'roles',
        name: 'roles',
        component: async () => import('@/views/RbacView.vue'),
        meta: { title: 'Roles', permission: 'roles.manage' },
      },
      {
        path: 'gateways',
        name: 'gateways',
        component: async () => import('@/views/GatewaysView.vue'),
        meta: { title: 'Payment gateways', permission: 'payments.gateways' },
      },
      {
        path: 'reference',
        name: 'reference',
        component: async () => import('@/views/ReferenceView.vue'),
        meta: { title: 'Languages & regions', permission: 'reference.manage' },
      },
      {
        path: 'content',
        name: 'content',
        component: async () => import('@/views/ContentView.vue'),
        meta: { title: 'Banners & news', permission: 'content.manage' },
      },
      {
        path: 'gifts',
        name: 'gifts',
        component: async () => import('@/views/GiftsView.vue'),
        meta: { title: 'Gift catalog', permission: 'content.manage' },
      },
      {
        path: 'kyc',
        name: 'kyc',
        component: async () => import('@/views/KycView.vue'),
        meta: { title: 'KYC review', permission: 'kyc.review' },
      },
      {
        path: 'money',
        name: 'money',
        component: async () => import('@/views/MoneyView.vue'),
        meta: { title: 'Money & commission', permission: 'analytics.view' },
      },
      {
        path: 'live-ops',
        name: 'live-ops',
        component: async () => import('@/views/LiveOpsView.vue'),
        meta: { title: 'Live ops', permission: 'analytics.view' },
      },
      {
        path: 'monitor/:id',
        name: 'monitor',
        component: async () => import('@/views/MonitorView.vue'),
        meta: { title: 'Monitor', permission: 'moderation.review' },
      },
      {
        path: 'campaigns',
        name: 'campaigns',
        component: async () => import('@/views/CampaignsView.vue'),
        meta: { title: 'Campaigns', permission: 'campaigns.manage' },
      },
      {
        path: 'audit',
        name: 'audit',
        component: async () => import('@/views/AuditView.vue'),
        meta: { title: 'Audit log', permission: 'analytics.view' },
      },
      {
        path: 'rooms',
        name: 'rooms',
        component: async () => import('@/views/RoomsView.vue'),
        meta: { title: 'Rooms', permission: 'analytics.view' },
      },
      {
        path: 'account',
        name: 'account',
        component: async () => import('@/views/AccountView.vue'),
        meta: { title: 'My account' },
      },
      {
        path: 'offers',
        name: 'offers',
        component: async () => import('@/views/OffersView.vue'),
        meta: { title: 'Recharge offers', permission: 'payments.gateways' },
      },
      {
        path: 'coin-packs',
        name: 'coin-packs',
        component: async () => import('@/views/CoinPackagesView.vue'),
        meta: { title: 'Coin packs', permission: 'payments.gateways' },
      },
      {
        path: 'support',
        name: 'support',
        component: async () => import('@/views/SupportView.vue'),
        meta: { title: 'Support desk', permission: 'support.manage' },
      },
    ],
  },
  { path: '/:pathMatch(.*)*', redirect: '/' },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  await auth.restore();

  if (to.meta['public'] === true) {
    return auth.isAuthenticated && to.name === 'login' ? { name: 'dashboard' } : true;
  }
  if (!auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }

  const required = to.meta['permission'];
  if (typeof required === 'string' && !auth.can(required)) {
    return { name: 'dashboard' };
  }
  return true;
});

router.afterEach((to) => {
  const title = typeof to.meta['title'] === 'string' ? to.meta['title'] : 'Admin';
  document.title = `${title} · Chametw Admin`;
});
